/**
 * lib/datasets/articles.ts
 *
 * Articles registry for Meazora.
 * Defines the schemas for articles, imports individual article files, and exports
 * the raw lists and summary structures for the Articles Hub and Detail views.
 */

export interface ArticleAuthor {
  name: string;
  url?: string;
}

export interface ArticleSource {
  name: string;
  url: string;
}

export interface Article {
  slug: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  author: ArticleAuthor;
  createdAt: string;
  updatedAt: string;
  readTime: string;
  keyTakeaway: string;
  sources: ArticleSource[];
  content: string;
  keyMetrics?: { label: string; value: string }[];
  faqs?: { question: string; answer: string }[];
  howToSteps?: { name: string; text: string }[];
  isNew?: boolean;
  lastVerified?: string;
}

export interface ArticleSummary {
  slug: string;
  title: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt?: string;
  readTime: string;
  isNew?: boolean;
  tags?: string[];
  author: ArticleAuthor;
}

// Import individual articles
import { articleRoofLifespan } from "./articles/roof-lifespan-by-material";
import { articleSignsNeedNewRoof } from "./articles/signs-need-new-roof";
import { articleHvacReplacementCost } from "./articles/hvac-replacement-cost-guide";
import { articleSignsAcDying } from "./articles/signs-ac-dying";
import { articleHeatPumpVsCentralAc } from "./articles/heat-pump-vs-central-ac";
import { articleKitchenRemodelCost } from "./articles/kitchen-remodel-cost-guide";
import { articleBathroomRemodelCost } from "./articles/bathroom-remodel-cost-guide";
import { articleRenovationsAddResaleValue } from "./articles/renovations-add-resale-value";
import { articleSolarPanelCosts } from "./articles/solar-panel-costs";
import { articleSolarPaybackPeriod } from "./articles/solar-payback-period";
import { articleWindowReplacementCost } from "./articles/window-replacement-cost-guide";
import { articleDeckConstructionCost } from "./articles/deck-construction-cost";
import { articleHowMuchConcreteNeed } from "./articles/how-much-concrete-need";
import { articleHowMuchPaintNeed } from "./articles/how-much-paint-need";
import { articleFlooringTypeComparison } from "./articles/flooring-type-comparison";
import { articleFlooringInstallationCost } from "./articles/flooring-installation-cost";
import { articleWaterHeaterLifespan } from "./articles/water-heater-lifespan";
import { articleBestTimeForRenovations } from "./articles/best-time-for-renovations";
import { articleFenceTypeComparison } from "./articles/fence-type-comparison";

// Import 19 new articles
import { articleWhyIsMyElectricBillSoHighThisSummer } from "./articles/why-is-my-electric-bill-so-high-this-summer";
import { articleWillNewAcLowerElectricBill } from "./articles/will-new-ac-lower-electric-bill";
import { articleHowMuchAtticInsulationDoYouNeed } from "./articles/how-much-attic-insulation-do-you-need";
import { articleSeerRatingExplainedAcBill } from "./articles/seer-rating-explained-ac-bill";
import { articleSmartThermostatSavingsGuide } from "./articles/smart-thermostat-savings-guide";
import { articleHurricaneSeasonHomePrep } from "./articles/2026-hurricane-season-home-prep";
import { articleDoesHomeownersInsuranceCoverRoofReplacement } from "./articles/does-homeowners-insurance-cover-roof-replacement";
import { articleImpactWindowsVsHurricaneShuttersCost } from "./articles/impact-windows-vs-hurricane-shutters-cost";
import { articleHowToPhotographRoofForInsuranceClaim } from "./articles/how-to-photograph-roof-for-insurance-claim";
import { articleWindRatedGarageDoorsGuide } from "./articles/wind-rated-garage-doors-guide";
import { articlePreListingHomeInspectionChecklist } from "./articles/pre-listing-home-inspection-checklist";
import { articleCostToPaintHouseExterior } from "./articles/cost-to-paint-house-exterior";
import { articleDrivewaySealingCostGuide } from "./articles/driveway-sealing-cost-guide";
import { articleInGroundVsAboveGroundPoolCost } from "./articles/in-ground-vs-above-ground-pool-cost";
import { articleCostToSodOrReseedLawn } from "./articles/cost-to-sod-or-reseed-lawn";
import { articleGutterReplacementCostGuide } from "./articles/gutter-replacement-cost-guide";
import { articleSidingReplacementCostGuide } from "./articles/siding-replacement-cost-guide";
import { articleIsHomeEnergyAuditWorthIt } from "./articles/is-home-energy-audit-worth-it";
import { articleBasementWaterproofingCostGuide } from "./articles/basement-waterproofing-cost-guide";
import { articleFloridaGarageDoorWindCode } from "./articles/florida-garage-door-wind-code";
import { articleCompleteKitchenRemodelGuide } from "./articles/complete-kitchen-remodel-guide";
import { articleCompleteHvacGuide } from "./articles/complete-hvac-guide";
import { articleCompleteFlooringGuide } from "./articles/complete-flooring-guide";
import { articleCompleteBathroomGuide } from "./articles/complete-bathroom-guide";
import { articleCompleteRoofingGuide } from "./articles/complete-roofing-guide";
import { articleCompletePaintingGuide } from "./articles/complete-painting-guide";
import { articleCompleteConcreteGuide } from "./articles/complete-concrete-guide";

// Register all articles in a central list
export const articles: Article[] = [
  articleRoofLifespan,
  articleSignsNeedNewRoof,
  articleHvacReplacementCost,
  articleSignsAcDying,
  articleHeatPumpVsCentralAc,
  articleKitchenRemodelCost,
  articleBathroomRemodelCost,
  articleRenovationsAddResaleValue,
  articleSolarPanelCosts,
  articleSolarPaybackPeriod,
  articleWindowReplacementCost,
  articleDeckConstructionCost,
  articleHowMuchConcreteNeed,
  articleHowMuchPaintNeed,
  articleFlooringTypeComparison,
  articleFlooringInstallationCost,
  articleWaterHeaterLifespan,
  articleBestTimeForRenovations,
  articleFenceTypeComparison,
  articleWhyIsMyElectricBillSoHighThisSummer,
  articleWillNewAcLowerElectricBill,
  articleHowMuchAtticInsulationDoYouNeed,
  articleSeerRatingExplainedAcBill,
  articleSmartThermostatSavingsGuide,
  articleHurricaneSeasonHomePrep,
  articleDoesHomeownersInsuranceCoverRoofReplacement,
  articleImpactWindowsVsHurricaneShuttersCost,
  articleHowToPhotographRoofForInsuranceClaim,
  articleWindRatedGarageDoorsGuide,
  articlePreListingHomeInspectionChecklist,
  articleCostToPaintHouseExterior,
  articleDrivewaySealingCostGuide,
  articleInGroundVsAboveGroundPoolCost,
  articleCostToSodOrReseedLawn,
  articleGutterReplacementCostGuide,
  articleSidingReplacementCostGuide,
  articleIsHomeEnergyAuditWorthIt,
  articleBasementWaterproofingCostGuide,
  articleFloridaGarageDoorWindCode,
  articleCompleteKitchenRemodelGuide,
  articleCompleteHvacGuide,
  articleCompleteFlooringGuide,
  articleCompleteBathroomGuide,
  articleCompleteRoofingGuide,
  articleCompletePaintingGuide,
  articleCompleteConcreteGuide,
];

// Map articles list to high-efficiency summaries for grid/hub page
export const articlesSummary: ArticleSummary[] = articles.map((a) => ({
  slug: a.slug,
  title: a.title,
  description: a.description,
  category: a.category,
  createdAt: a.createdAt,
  updatedAt: a.updatedAt,
  readTime: a.readTime,
  isNew: a.isNew,
  tags: a.tags,
  author: a.author,
}));
